/* eslint-disable */
/**
 * Fabric.js v5 → v7 compatibility shim (KL-13820).
 *
 * Wraps fabric@^7.3.1 so existing code written against the v5 namespace API
 * keeps compiling and running without a wide refactor. Re-exports the legacy
 * `fabric` namespace (both value and types) and bridges the breaking changes:
 *
 * - `fabric.Image` ⇄ `FabricImage` (renamed export)
 * - `fabric.Object` ⇄ `FabricObject` (renamed export)
 * - `fabric.Image.fromURL(url, cb, opts)` callback-style → promise-based fromURL
 * - `fabric.loadSVGFromURL(url, cb)` callback-style → promise-based loader
 * - `fabric.util.createClass(Parent, proto)` removed → ES6 `extends` polyfill
 * - `fabric.Image.filters` static accessor → re-attached for legacy access
 *
 * Source: https://fabricjs.com/docs/upgrading/upgrading-to-fabric-60/
 */

import * as F from 'fabric';

// === Restore v5 origin defaults (left/top instead of v6+ center/center) ===
// Klikkie editor code positions every fabric object via {left, top, width,
// height} assuming origin = top-left (the v5 default). v6/v7 changed the
// default to 'center/center', which shifts every implicitly-positioned
// object up-left by half its size. Visible effects so far:
//   • BitmapEditor placeholder rects mis-aligned vs their SVG icons (icons
//     have origin explicit, rects don't)
//   • PictureEditor uploaded photos partially clipped against the canvas
//
// FabricObject.ownDefaults is the canonical source of these defaults (every
// constructor does `Object.assign(this, FabricObject.ownDefaults)` —
// verified in node_modules/fabric/dist/src/shapes/Object/Object.mjs:93).
// Mutating it once at shim-load time flips the defaults globally for every
// subclass (Rect, Image, Line, Textbox, IText, ...) without touching any
// editor code.
try {
  const defaults: any = (F.FabricObject as any).ownDefaults;
  if (defaults) {
    defaults.originX = 'left';
    defaults.originY = 'top';
  }
} catch {
  /* best-effort */
}

// === Force the Canvas2D filter backend (disable WebGL filtering) ===
// v7's WebGL filter backend has a regression for images larger than its
// `textureSize` tile (default 4096): tile-stitching produces a result
// canvas smaller than the source, so applying a filter to a 6016×4000
// photo leaves half the image area unfiltered (canvas background bleeds
// through). Bumping textureSize to 16384 did NOT fix it — the bug isn't
// just the tile boundary, the WebGL pipeline itself ends up with wrong
// output dimensions in v7.
//
// The 2D filter backend (Canvas2dFilterBackend) operates on an ImageData
// buffer sized exactly to the source, so it produces correctly-sized
// output for any input. It's slower than WebGL on small images but
// correct for our case (Klikkie photos are 4-50 MP DSLR / phone shots).
// PictureEditor only uses Grayscale, which is trivially cheap in 2D.
//
// `enableGLFiltering = false` makes `getFilterBackend()` return the 2D
// backend (verified in node_modules/fabric/dist/src/filters/FilterBackend.mjs).
try {
  (F as any).config?.configure?.({ enableGLFiltering: false });
} catch {
  /* fabric.config may not be present on older builds — best-effort */
}

// === Dev-only Canvas subclass that exposes itself on window for inspection ===
// We don't touch the real F.Canvas — instead we hand callers a thin subclass
// via the shim's exported `fabric.Canvas`. Every `new fabric.Canvas(...)`
// invocation (the only entry point in this codebase) records `window.__fc =
// this`, so console snippets like `__fc.getObjects()` always work without
// having to dig into React internals. Inert in production builds.
const ExposedCanvas =
  typeof window !== 'undefined' && process.env.NODE_ENV !== 'production'
    ? class extends F.Canvas {
        constructor(...args: ConstructorParameters<typeof F.Canvas>) {
          super(...args);
          try {
            (window as any).__fc = this;
          } catch {
            /* no-op */
          }
        }
      }
    : F.Canvas;

// === Restore v5 method aliases that v6/v7 removed ===
//
// v5 had several `canvas.<verb>(obj)` methods that v7 kept as
// `canvas.<verb>Object(obj)` (or renamed entirely). Verified against
// `node_modules/fabric/dist/src/Collection.mjs` — the v7 API actually has
// `sendObjectToBack` / `bringObjectToFront` / `sendObjectBackwards` /
// `bringObjectForward` / `moveObjectTo`, NOT the unprefixed names that the
// official upgrade guide implies. Same story with `observe` → `on` and
// `unobserve` → `off`.
//
// We add the v5 names as prototype-level aliases so existing callsites
// (PictureEditor.observe x3, PictureEditor.sendToBack) keep working without
// rewriting any business logic.
{
  const proto: any = (F.Canvas as any).prototype;
  const alias = (legacy: string, current: string) => {
    if (typeof proto[legacy] !== 'function' && typeof proto[current] === 'function') {
      proto[legacy] = proto[current];
    }
  };
  alias('observe', 'on');
  alias('unobserve', 'off');
  alias('sendToBack', 'sendObjectToBack');
  alias('bringToFront', 'bringObjectToFront');
  alias('sendBackwards', 'sendObjectBackwards');
  alias('bringForward', 'bringObjectForward');
  // Note: v5 `canvas.moveTo(obj, idx)` shape — v7's `moveObjectTo` matches.
  alias('moveTo', 'moveObjectTo');
}

// === Restore v5 method chaining ===
// v6/v7 deprecated chaining on mutators (`set`, `setCoords`, `rotate`, ...). The
// methods still exist but now return `undefined` instead of `this`. The old
// code base relies on at least one chained call, e.g.
//   imageOnCanvas.setCoords()._element.naturalWidth     // PictureEditor.jsx:236
// which used to be valid because `setCoords()` returned `this`.
//
// Wrapping these on FabricObject.prototype keeps the side effects intact and
// returns `this`, restoring the v5 chaining contract everywhere transparently.
{
  const objProto: any = (F.FabricObject as any).prototype;
  const restoreChain = (name: string) => {
    const original = objProto[name];
    if (typeof original !== 'function') return;
    if ((original as any).__compatChained) return;
    const wrapped = function (this: any, ...args: any[]) {
      original.apply(this, args);
      return this;
    };
    (wrapped as any).__compatChained = true;
    objProto[name] = wrapped;
  };
  // Only `setCoords` is actually chained in this code base
  // (PictureEditor.jsx:236-237). `set`/`rotate`/`setOptions` aren't chained
  // anywhere (verified by grep), so we leave them alone to avoid hiding any
  // useful v7 return values they might produce.
  restoreChain('setCoords');
}

// === Restore v5 animate(name, value, opts) signature ===
// v5: object.animate(property, targetValue, options)
// v7: object.animate({ [property]: targetValue }, options)
//
// PictureEditor uses the v5 signature at rotateImage (animate('angle', 90,
// {...}) — rotate button silently no-ops under v7), limitObjectToCanvas-
// Boundaries (×4 animate('left'|'top', ..., {...})), and bindZingZoom. v7's
// animate() does Object.entries(arg1).reduce(...), which on a string arg1
// like 'angle' iterates the *characters* and the animation never fires.
//
// Detect the legacy call shape (first arg is a string) and rewrite it to
// the v7 object form. Leaves modern usages untouched.
{
  const objProto: any = (F.FabricObject as any).prototype;
  const origAnimate = objProto.animate;
  if (typeof origAnimate === 'function' && !(origAnimate as any).__compatAnimate) {
    const wrapped = function (this: any, ...args: any[]) {
      if (typeof args[0] === 'string') {
        const [prop, value, opts] = args;
        return origAnimate.call(this, { [prop]: value }, opts);
      }
      return origAnimate.apply(this, args);
    };
    (wrapped as any).__compatAnimate = true;
    objProto.animate = wrapped;
  }
}

// === Image: promise→callback wrapper + filters re-attachment ===

const CompatImage: any = class extends (F.FabricImage as any) {
  // v5 callback signature shimmed onto v7's promise-only fromURL. Declared
  // as `any` so TS doesn't try to match the parent's strict overload set.
  static fromURL(
    url: string,
    callbackOrOptions?: ((img: any) => void) | any,
    maybeOptions?: any
  ): any {
    let cb: ((img: any) => void) | undefined;
    let opts: any;
    if (typeof callbackOrOptions === 'function') {
      cb = callbackOrOptions;
      opts = maybeOptions;
    } else {
      opts = callbackOrOptions;
    }
    const promise = (F.FabricImage as any).fromURL(url, opts);
    if (cb && promise && typeof promise.then === 'function') {
      promise.then(cb).catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('[fabricCompat] Image.fromURL failed:', err);
      });
    }
    return promise;
  }
};
CompatImage.filters = (F as any).filters;

// === SVG loader: promise→callback wrapper ===

const loadSVGFromURLCompat = (
  url: string,
  callback?: (objects: F.FabricObject[], options: any) => void
): Promise<any> => {
  const promise = (F as any).loadSVGFromURL(url);
  if (callback) {
    promise
      .then((result: any) => {
        const objects = result?.objects ?? result;
        const options = result?.options ?? {};
        callback(objects, options);
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('[fabricCompat] loadSVGFromURL failed:', err);
      });
  }
  return promise;
};

// === util.createClass: ES6-class polyfill ===
//
// v5's `fabric.util.createClass(Parent, proto)` produced subclasses whose
// instances had a `callSuper(methodName, ...args)` method that invoked the
// parent class's same-named method. v7 dropped both `createClass` and
// `callSuper` — subclasses are expected to use ES6 `extends` + `super.x(...)`.
//
// LimitedTextbox / LimitedIText still use `this.callSuper('initialize'|'setText'|
// 'onInput'|'onKeyDown'|'onBlur'|'_render', ...)`. We mirror the v5 contract:
// callSuper resolves up the prototype chain past the proto-injected override
// and dispatches to whatever the *original* parent had at that key. If the v7
// parent no longer defines the method (e.g. v5's `setText` is now `set('text',
// ...)`), we silently fall back to `set(methodName, args[0])` for the `setText`
// case and a no-op otherwise, so existing call sites keep working.

const createClass = (Parent: any, proto: any) => {
  class Generated extends Parent {
    constructor(...args: any[]) {
      super(...args);
      if (typeof (this as any).initialize === 'function') {
        (this as any).initialize(...args);
      }
    }

    callSuper(methodName: string, ...args: any[]) {
      const parentProto = Parent && Parent.prototype;
      if (parentProto && typeof parentProto[methodName] === 'function') {
        return parentProto[methodName].apply(this, args);
      }
      // v7 dropped some v5 helpers — translate the ones we know about.
      if (methodName === 'setText' && typeof (this as any).set === 'function') {
        return (this as any).set('text', args[0]);
      }
      // No-op fallback so missing legacy methods don't crash editors.
      return undefined;
    }
  }
  // eslint-disable-next-line no-restricted-syntax
  for (const key of Object.keys(proto)) {
    (Generated.prototype as any)[key] = proto[key];
  }
  return Generated;
};

// v5 `fabric.util.loadImage(url, callback, context, crossOrigin)` was a
// callback-style helper. v7 turned it into `loadImage(url, options): Promise`.
// PictureEditor.loadImageOnCanvas still calls it as
// `new fabric.util.loadImage(data, callback, null, { crossOrigin: 'anonymous' })`
// — i.e., callback as second arg, AND the `new` keyword. Arrow functions have
// no [[Construct]] slot, so the shim must be a plain `function` declaration
// so `new loadImageCompat(...)` doesn't throw "is not a constructor". Shim
// dispatches on the second-arg type so both v5 (callback) and v7 (options)
// shapes work.
function loadImageCompat(
  this: any,
  url: string,
  callbackOrOptions?: ((img: HTMLImageElement | null) => void) | any,
  _context?: any,
  crossOriginOrOptions?: any
): Promise<HTMLImageElement | null> {
  let cb: ((img: HTMLImageElement | null) => void) | undefined;
  let opts: any;
  if (typeof callbackOrOptions === 'function') {
    cb = callbackOrOptions;
    // v5 passed `crossOrigin` as a string or `{ crossOrigin }`.
    opts =
      typeof crossOriginOrOptions === 'string'
        ? { crossOrigin: crossOriginOrOptions }
        : crossOriginOrOptions;
  } else {
    opts = callbackOrOptions;
  }
  const promise = (F as any).util.loadImage(url, opts);
  if (cb && promise && typeof promise.then === 'function') {
    promise
      .then((img: HTMLImageElement) => cb && cb(img))
      .catch(() => cb && cb(null));
  }
  return promise;
}

const utilCompat = {
  ...(F as any).util,
  createClass,
  loadImage: loadImageCompat,
};

// === Public namespace export ===

export const fabric = {
  Canvas: ExposedCanvas,
  Rect: F.Rect,
  Line: F.Line,
  Textbox: F.Textbox,
  IText: F.IText,
  // Typed as `any` so callsites keep using v5's callback signatures without
  // TS rejecting them against v7's promise-only overloads.
  Image: CompatImage as any,
  Object: F.FabricObject,
  util: utilCompat,
  loadSVGFromURL: loadSVGFromURLCompat as any,
  filters: (F as any).filters,
};

export namespace fabric {
  export type Canvas = F.Canvas;
  export type Image = F.FabricImage;
  // v5's `fabric.Object` was the canonical base class; v7's FabricObject is the
  // direct rename. Consumers that use it for typing get the same surface.
  export type Object = F.FabricObject;
  export type Textbox = F.Textbox;
  export type IText = F.IText;
  export type Rect = F.Rect;
  export type Line = F.Line;
  /**
   * v5's `IEvent` mapped onto v7's pointer-event payload. We expose a
   * structural shape that matches the fields the current code base reads
   * (`e`, `target`, `pointer`). If v7 introduces additional fields they can
   * be added here without touching call sites.
   */
  export type IEvent<T = MouseEvent> = {
    e: T;
    target?: F.FabricObject;
    pointer?: { x: number; y: number };
    selected?: F.FabricObject[];
    deselected?: F.FabricObject[];
  };
  /**
   * Legacy alias used only in JSDoc within PictureEditor.jsx. Maps to the
   * concrete base type that callers expect.
   */
  export type Klass = F.FabricObject;
}
